<template>
  <Modal title="New play" @close="onClose">
    <div class="newPlayForm">
      <label class="newPlayField">
        <span class="newPlayFieldLabel">Name</span>
        <input
          class="newPlayInput"
          type="text"
          v-model="name"
          placeholder="e.g. AI security, founder-led"
          maxlength="200"
          @keydown.enter="create"
        />
      </label>
      <p v-if="error" class="newPlayError">{{ error }}</p>
    </div>
    <template #footer>
      <button class="btn secondary" @click="onClose">Cancel</button>
      <button class="btn primary" :disabled="!name.trim() || saving" @click="create">
        {{ saving ? 'Creating…' : 'Create' }}
      </button>
    </template>
  </Modal>
</template>

<script setup lang="ts">
import { ref } from 'vue'
import Modal from './Modal.vue'
import { apiFetch } from '../../services/api'

const emit = defineEmits<{
  (e: 'close'): void
  (e: 'created', play: { id?: string; name: string }): void
}>()

const name = ref('')
const saving = ref(false)
const error = ref('')

function onClose() {
  emit('close')
}

async function create() {
  const trimmed = name.value.trim()
  if (!trimmed || saving.value) return
  saving.value = true
  error.value = ''
  try {
    const resp = await apiFetch('/sales-plays', {
      method: 'POST',
      body: { name: trimmed },
    })
    emit('created', { id: resp?.play?.id, name: trimmed })
    emit('close')
  } catch (e: any) {
    error.value = e?.message || 'Failed to create play'
  } finally {
    saving.value = false
  }
}
</script>

<style scoped>
.newPlayForm {
  display: flex;
  flex-direction: column;
  gap: var(--space-4);
}
.newPlayField {
  display: flex;
  flex-direction: column;
  gap: var(--space-2);
}
.newPlayFieldLabel {
  font-family: var(--font-sans);
  font-size: var(--fs-xs);
  font-weight: var(--fw-semibold);
  color: var(--color-text-secondary);
}
.newPlayInput {
  height: 36px;
  padding: 0 var(--space-3);
  border: 1px solid var(--color-border-subtle);
  border-radius: var(--radius-md);
  background: var(--color-bg-app);
  color: var(--color-text-primary);
  font-family: var(--font-sans);
  font-size: var(--fs-sm);
}
.newPlayInput:focus {
  outline: none;
  border-color: var(--color-accent);
}
.newPlayError {
  margin: 0;
  font-family: var(--font-sans);
  font-size: var(--fs-sm);
  color: var(--color-danger);
}
</style>
